import type {
    MaterialCardItemProps,
    MaterialCategoryRow as ApiMaterialCategoryRow,
    ApiMaterialLineItemOption,
    MaterialLineItemOption,
    MaterialPriceRange,
} from "../types";

export type MaterialCardItem = MaterialCardItemProps & {
    id: string;
    category: string;
    mode: "single";
};

export type MaterialMultiLineItem = {
    id: string;
    category: string;
    mode: "multi";
    derivedFrom?: string;
    sourceInspectionAttribute?: string;
    lines: {
        lineId: string;
        size?: string;
        type?: string;
        flashingKit?: string;
        availableProducts: MaterialLineItemOption[];
        selectedProductId: string | null;
        quantity: number;
        priceRange: MaterialPriceRange | null;
        colors: string[];
    }[];
};

export type MaterialRowItem = MaterialCardItem | MaterialMultiLineItem;

export type FurnaceVentMaterialRow = MaterialMultiLineItem & {
    derivedFrom: "Furnace Vent";
};

export type MaterialTreeLineLeaf = {
    lineId: string;
    category: string;
    availableProducts: MaterialLineItemOption[];
    selectedProductId: string | null;
    quantity: number;
    priceRange: MaterialPriceRange | null;
    type?: string;
    flashingKit?: string;
    colors?: string[];
};

export type MaterialTreeSizeBranch = {
    size: string;
    lines: MaterialTreeLineLeaf[];
};

const FURNACE_SIZE_ORDER = ["4 inch", "5 inch", "6 inch", "7 inch", "TBD"];
const FURNACE_CATEGORY_ORDER = ["Furnace Jacks", "Furnace Cap", "Furnace Collar"];

function compareOrderedSizes(a: string, b: string, order: string[]): number {
    const idxA = order.indexOf(a);
    const idxB = order.indexOf(b);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.localeCompare(b);
}

function compareOrderedCategories(
    a: string,
    b: string,
    order: string[],
): number {
    const idxA = order.indexOf(a);
    const idxB = order.indexOf(b);
    return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
}

export function buildMaterialTreeBySize(
    rows: MaterialMultiLineItem[],
    options: {
        sizeOrder: string[];
        categoryOrder: string[];
    },
): MaterialTreeSizeBranch[] {
    const sizeMap = new Map<string, MaterialTreeLineLeaf[]>();

    for (const row of rows) {
        for (const line of row.lines) {
            if (!line.size) continue;
            const size = line.size.trim();
            if (!sizeMap.has(size)) sizeMap.set(size, []);
            sizeMap.get(size)!.push({
                lineId: line.lineId,
                category: row.category,
                availableProducts: line.availableProducts,
                selectedProductId: line.selectedProductId,
                quantity: line.quantity,
                priceRange: line.priceRange,
                type: line.type,
                flashingKit: line.flashingKit,
                colors: line.colors,
            });
        }
    }

    return Array.from(sizeMap.entries())
        .sort(([a], [b]) => compareOrderedSizes(a, b, options.sizeOrder))
        .map(([size, lines]) => ({
            size,
            lines: lines.sort((a, b) =>
                compareOrderedCategories(a.category, b.category, options.categoryOrder),
            ),
        }));
}

export function buildFurnaceVentTreeBySize(rows: FurnaceVentMaterialRow[]): MaterialTreeSizeBranch[] {
    return buildMaterialTreeBySize(rows, {
        sizeOrder: FURNACE_SIZE_ORDER,
        categoryOrder: FURNACE_CATEGORY_ORDER,
    });
}

/** Map API size-option → UI option; ProductId defaults to first variant until color UI lands. */
function mapApiOption(option: ApiMaterialLineItemOption): MaterialLineItemOption {
    const variants = Array.isArray(option.Variants) ? option.Variants : [];
    const primary = variants[0];

    return {
        ProductId: primary?.ProductId ?? option.OptionKey,
        OptionKey: option.OptionKey,
        ProductName: option.ProductName,
        Subcategory: option.Subcategory,
        ManufactureName: option.ManufactureName,
        ManufactureId: option.ManufactureId,
        CategoryName: option.CategoryName,
        MinPrice: option.PriceRange?.Min ?? primary?.MinPrice ?? null,
        MaxPrice: option.PriceRange?.Max ?? primary?.MaxPrice ?? null,
        Variants: variants,
    };
}

export function mapMaterialLineItemsResponse(
    res: Record<string, ApiMaterialCategoryRow[]>,
): Record<string, MaterialRowItem[]> {
    const grouped: Record<string, MaterialRowItem[]> = {};

    for (const [groupName, rows] of Object.entries(res)) {
        grouped[groupName] = rows.map(mapCategoryRow);
    }

    return grouped;
}

function mapCategoryRow(row: ApiMaterialCategoryRow): MaterialRowItem {
    if (row.Mode === "multi") {
        return {
            id: row.Id,
            category: row.Category,
            mode: "multi",
            derivedFrom: row.DerivedFrom,
            sourceInspectionAttribute: row.SourceInspectionAttribute,
            lines: row.Lines.map((line) => ({
                lineId: line.LineId,
                size: line.Size,
                type: line.Type,
                flashingKit: line.FlashingKit,
                availableProducts: (line.Options || []).map(mapApiOption),
                selectedProductId: line.SelectedProductId,
                quantity: line.Quantity === null ? 0 : line.Quantity,
                priceRange: line.PriceRange,
                colors: line.Colors || [],
            })),
        };
    }

    return {
        id: row.Id,
        category: row.Category,
        productName: row.Category,
        mode: "single",
        availableProducts: (row.Options || []).map(mapApiOption),
        selectedProductId: row.SelectedProductId,
        quantity: row.Quantity === null ? 0 : row.Quantity,
        priceRange: row.PriceRange,
        colors: row.Colors || [],
    };
}

export function buildMaterialEditsFromRows(materialsByGroup: any): any {
    const edits: any = {};

    for (const rows of Object.values(materialsByGroup) as any[]) {
        for (const row of rows) {
            if (row.mode === "single") {
                edits[row.id] = {
                    selectedProductId: row.selectedProductId,
                    quantity: row.quantity,
                };
                continue;
            }

            for (const line of row.lines) {
                edits[line.lineId] = {
                    selectedProductId: line.selectedProductId,
                    quantity: line.quantity,
                };
            }
        }
    }

    return edits;
}
